import { useEffect, useContext, useState, FC, useMemo } from 'react'
import { useRouter } from 'next/router'
import { Link } from '@chakra-ui/next-js'
import { Button, Center, Box, Fade } from '@chakra-ui/react'

import { useAuthentication } from 'contexts/Authentication/Provider'
import { CustomFeedback, Loader } from 'components'
import { getStorage } from 'utils/storage'
import { USER_TOKEN_STORAGE_NAME } from 'constants/index'
import UserPermissionsManagerContext from './Context'
import { APP_CITIZEN_URL } from 'constants/index'
import getLoggedUserPermissions from './services/calls/getLoggedUserPermissions'

type Props = {
  publicModules?: string[]
  publicPages?: string[]
  children: JSX.Element
}

const UserPermissionsManagerProvider: FC<Props> = ({ publicModules = [], children }) => {
  const { user } = useAuthentication()
  const [loadingUserPermissions, setLoadingUserPermissions] = useState(true)
  const [userPermissions, setUserPermissions] = useState<string[]>([])
  const [view, setView] = useState<string | null>(null)
  const router = useRouter()

  const isPublicModule = useMemo(() => {
    return publicModules.some((modulePath) => {
      return router.asPath.indexOf(modulePath) !== -1
    })
  }, [router.asPath])

  async function loadLoggedUserPermissions() {
    setLoadingUserPermissions(true)
    const response = await getLoggedUserPermissions()

    if (response?.header?.success) {
      const permissions: string[] = response.body.permissions
      if (permissions.length > 0) {
        setUserPermissions(permissions)
      } else {
        setView('NO_PERMISSIONS')
      }
    } else {
      setView('NO_PERMISSIONS')
    }
    setLoadingUserPermissions(false)
  }

  useEffect(() => {
    const userToken = getStorage(USER_TOKEN_STORAGE_NAME)
    if (userToken) {
      loadLoggedUserPermissions()
    } else {
      setLoadingUserPermissions(false)
    }
  }, [user])

  if (loadingUserPermissions) {
    return (
      <Center>
        <Loader text="Carregando permissões do usuário" height="100vh" />
      </Center>
    )
  }

  if (!isPublicModule && view === 'NO_PERMISSIONS') {
    return (
      <Center height="100vh">
        <CustomFeedback context="withoutPermission">
          <Box>
            <Link href={APP_CITIZEN_URL}>
              <Button w="full" size="lg" mb={4}>
                Ir para o APP Cidadão
              </Button>
            </Link>
            <Button w="full" size="lg" variant="ghost" onClick={() => router.back()}>
              Voltar
            </Button>
          </Box>
        </CustomFeedback>
      </Center>
    )
  }
  if (!isPublicModule && view === 'ONLY_MODULE_PERMISSION') {
    return (
      <Center height="100vh">
        <CustomFeedback context="onlyModulePermissions">
          <Box>
            <Link href={APP_CITIZEN_URL}>
              <Button w="full" size="lg" mb={4}>
                Ir para o APP Cidadão
              </Button>
            </Link>
            <Button w="full" size="lg" variant="ghost" onClick={() => router.back()}>
              Voltar
            </Button>
          </Box>
        </CustomFeedback>
      </Center>
    )
  }

  return (
    <UserPermissionsManagerContext.Provider value={{ userPermissions, setUserPermissions }}>
      <Fade in>{children}</Fade>
    </UserPermissionsManagerContext.Provider>
  )
}

export const useUserPermissionsManager = () => useContext(UserPermissionsManagerContext)

export default UserPermissionsManagerProvider
